// apps/client/src/utils/apiClient.ts

interface AuthInvalidationCallbacks {
  onAuthFailure: () => void;
  onOrganisationInvalidated: () => void;
}

class ApiClient {
  private baseURL: string;
  private authCallbacks: AuthInvalidationCallbacks | null = null;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  setAuthCallbacks(callbacks: AuthInvalidationCallbacks) {
    this.authCallbacks = callbacks;
  }

  async request(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const token = localStorage.getItem('token');

    const config: RequestInit = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    };

    const response = await fetch(`${this.baseURL}${endpoint}`, config);

    // Handle auth-related errors
    if (response.status === 401) {
      // Token expired or invalid
      this.authCallbacks?.onAuthFailure();
      throw new Error('Authentication failed');
    }

    if (response.status === 403) {
      const errorData = await response
        .clone()
        .json()
        .catch(() => ({}));

      // Check if it's an organisation-specific error
      if (
        errorData.code === 'ORGANISATION_ACCESS_DENIED' ||
        errorData.code === 'USER_NOT_IN_ORGANISATION'
      ) {
        this.authCallbacks?.onOrganisationInvalidated();
        throw new Error('Insufficient permissions');
      }
    }

    // Handle user deleted or deactivated
    if (response.status === 404 && endpoint.includes('/user/')) {
      const errorData = await response
        .clone()
        .json()
        .catch(() => ({}));

      if (errorData.code === 'USER_NOT_FOUND' || errorData.code === 'USER_DEACTIVATED') {
        this.authCallbacks?.onAuthFailure();
        throw new Error('User account no longer exists');
      }
    }

    return response;
  }

  // Convenience methods
  async get(endpoint: string) {
    return this.request(endpoint, { method: 'GET' });
  }

  async post(endpoint: string, data?: any) {
    return this.request(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put(endpoint: string, data?: any) {
    return this.request(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch(endpoint: string, data?: any) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete(endpoint: string) {
    return this.request(endpoint, { method: 'DELETE' });
  }
}

// Export singleton instance
export const apiClient = new ApiClient(process.env.REACT_APP_API_URL || '');
